import { useSearchParams } from 'react-router-dom'
import { ApiClient } from 'services/api-client'
import { AppButton, DotMatrix } from '@clickguard/design-system'
import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import { usePostHog } from 'posthog-js/react'
import { PlanSelectionCard } from './plan-selection'
import { PaymentOverview, TermsNote } from './payment-overview'
import { useBlackFridaySelection } from 'hooks/use-black-friday-selection'
import { NewSubscriptionBanner } from 'components/black-friday-banner'
import { PaymentForm, PaymentFormRef } from './payment-form'
import {
	planOptions,
	CUSTOM_PLAN_THRESHOLD,
	PLAN_SPEND_LIMITS
} from './data/plan-features'
import { PlanOption } from './plan-selection/types'
import { useBillingStore } from '../../../../../store/billing-store/billing-store'
import { Onboarding } from '../../../../../services/user'
import { parseError } from '../../../../../utils/errors'
import { useTrackPostHogEvent } from '../../../../../utils/posthog'
import { trackLeadFreeTrial } from '../../../../../utils/ga4-events'
import { PostHogEvent, PostHogPath } from '../../../../../utils/posthog-events'
import { AppliedCoupon, BillingFrequency } from './payment-overview/types'
import { OnboardingHeader } from '../../../../../components/onboarding-header'
import { FeatureComparison } from './feature-comparison'
import { GDialog } from '../../../../../components/basic-blocks/g-dialog/g-dialog'

const INTERVAL_MAPPING: Record<
	BillingFrequency,
	{ interval: string; interval_count: number }
> = {
	monthly: { interval: 'month', interval_count: 1 },
	quarterly: { interval: 'month', interval_count: 3 },
	annually: { interval: 'year', interval_count: 1 }
}

interface PlanPaymentPageProps {
	accountId?: string
	isAgency?: boolean
	initialAdSpend?: number
	initialPlanId?: string
	onTrialActivated?: () => Promise<void>
}

export const PlanPaymentPage = ({
	accountId,
	isAgency = false,
	initialAdSpend = 30,
	initialPlanId,
	onTrialActivated
}: PlanPaymentPageProps) => {
	const paymentFormRef = useRef<PaymentFormRef>(null)
	const paymentSectionRef = useRef<HTMLDivElement>(null)
	const billingStore = useBillingStore()
	const posthog = usePostHog()
	const trackPostHogEvent = useTrackPostHogEvent()

	const [searchParams] = useSearchParams()
	const heroOffer = searchParams.get('offer') === 'quarterly'
	const [heroOfferCode, setHeroOfferCode] = useState<string | null>(null)
	const autoAppliedOffer = useRef(false)
	const selectedFrequency = useRef<BillingFrequency>(
		heroOffer ? 'quarterly' : 'annually'
	)
	useEffect(() => {
		if (heroOffer)
			ApiClient.client
				.get('/system/onboarding-quarterly-offer')
				.then(({ data }) => {
					setHeroOfferCode(data.data.code)
					if (!data.data.code)
						setCouponError(
							'The special offer is not available yet. Please contact support.'
						)
				})
				.catch(() =>
					setCouponError('Could not load the special offer. Please try again.')
				)
	}, [heroOffer])
	// --- State ---
	const [adSpend, setAdSpend] = useState(
		initialAdSpend > CUSTOM_PLAN_THRESHOLD ? 101 : initialAdSpend
	)
	const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(
		heroOffer ? 'quarterly' : 'annually'
	)
	const [couponCode, setCouponCode] = useState('')
	const [isPaymentComplete, setIsPaymentComplete] = useState(false)
	// Submit-flow phase. Drives the CTA copy so users have continuous feedback
	// during multi-second waits instead of staring at "Processing..." for 15s
	// while the bank's 3DS modal loads.
	//   idle          — nothing in flight
	//   submitting    — initial POST to /trial/start or /quote/start
	//   verifying_3ds — handleNextAction is open (waiting on the bank/issuer)
	//   finalizing    — second POST after 3DS succeeded
	type SubmitPhase = 'idle' | 'submitting' | 'verifying_3ds' | 'finalizing'
	const [phase, setPhase] = useState<SubmitPhase>('idle')
	const isConfirming = phase !== 'idle'
	const submittingRef = useRef(false)
	const [customSpendValue, setCustomSpendValue] = useState(
		initialAdSpend > CUSTOM_PLAN_THRESHOLD ? initialAdSpend * 1000 : 100000
	)
	const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)
	const [couponLoading, setCouponLoading] = useState(false)
	const [couponError, setCouponError] = useState<string | null>(null)
	const [compareModalOpen, setCompareModalOpen] = useState(false)
	const [paymentError, setPaymentError] = useState<string | null>(null)
	// Currently selected PaymentElement tab ('card' | 'sepa_debit' | etc.).
	// Drives the conditional "$1 hold" disclaimer in PaymentOverview — only
	// cards get a pre-auth on the BE. Defaults to 'card' since the
	// PaymentElement also defaults to card on first render.
	const [selectedPmType, setSelectedPmType] = useState<string>('card')

	// Effective spend in thousands (resolves 101 → actual custom value)
	const effectiveSpend = adSpend

	// --- Derived state ---
	const recommendPlanFor = useCallback(
		(spend: number) => {
			if (spend > CUSTOM_PLAN_THRESHOLD) return 'custom'
			if (isAgency) return 'pro'
			if (spend <= PLAN_SPEND_LIMITS.lite) return 'lite'
			if (spend <= PLAN_SPEND_LIMITS.standard) return 'standard'
			return 'pro'
		},
		[isAgency]
	)
	const recommendedPlanId = useMemo(
		() => recommendPlanFor(effectiveSpend),
		[recommendPlanFor, effectiveSpend]
	)

	const eligiblePlans = useMemo((): PlanOption[] => {
		return planOptions.map((plan) => ({
			...plan,
			disabled: effectiveSpend > PLAN_SPEND_LIMITS[plan.id],
			disabledReason:
				effectiveSpend > PLAN_SPEND_LIMITS[plan.id]
					? `Limited to $${PLAN_SPEND_LIMITS[plan.id]}k spend protection`
					: undefined
		}))
	}, [effectiveSpend])

	const [selectedPlanId, setSelectedPlanId] = useState(() => {
		if (
			initialPlanId &&
			!(effectiveSpend > (PLAN_SPEND_LIMITS[initialPlanId] ?? Infinity))
		) {
			return initialPlanId
		}
		return recommendedPlanId
	})

	const isCustomPlan = selectedPlanId === 'custom'

	const selectedPlan =
		eligiblePlans.find((p) => p.id === selectedPlanId) || eligiblePlans[1]

	// --- Real pricing from billingStore ---
	const getPriceForPlan = useCallback(
		(planId: string, frequency: BillingFrequency, spendValue: number) => {
			const billingPlan = billingStore.plans.find((p) => p.tier === planId)
			if (!billingPlan) return null

			const { interval, interval_count } = INTERVAL_MAPPING[frequency]
			const prices = billingPlan.prices.filter(
				(p) => p.interval === interval && p.interval_count === interval_count
			)

			const spendAmount = spendValue * 1000
			let bestPrice = prices.find((p) => p.spend === spendAmount)
			if (!bestPrice) {
				// Fallback: closest price at or above spend
				const higher = prices
					.filter((p) => p.spend >= spendAmount)
					.sort((a, b) => a.spend - b.spend)
				bestPrice = higher[0] || prices[prices.length - 1]
			}

			return bestPrice
		},
		[billingStore.plans]
	)

	const currentPrice = useMemo(
		() => getPriceForPlan(selectedPlanId, billingFrequency, effectiveSpend),
		[getPriceForPlan, selectedPlanId, billingFrequency, effectiveSpend]
	)
	const campaign = useBlackFridaySelection({
		accountId,
		tier: selectedPlanId,
		priceId: isCustomPlan ? null : currentPrice?.price_id,
		spend: isCustomPlan ? customSpendValue : currentPrice?.spend || 0,
		yearly: billingFrequency === 'annually',
		onboarding: true,
		busy: isConfirming
	})
	const [bannerHeight, setBannerHeight] = useState(0)
	useEffect(() => {
		if (campaign.active) {
			setAppliedCoupon(null)
			setCouponCode('')
		}
	}, [campaign.active])
	const spendK =
		(isCustomPlan ? customSpendValue : currentPrice?.spend || 0) / 1000
	const adSpendLimit =
		campaign.active && spendK > 0
			? { standardK: spendK, boostedK: spendK * 3 }
			: null

	// Monthly equivalent for display
	const monthlyPriceDisplay = useMemo(() => {
		if (!currentPrice) return 0
		const monthlyVal =
			currentPrice.pricing_by_currency?.usd?.price_monthly ??
			currentPrice.price_monthly ??
			currentPrice.price
		return monthlyVal / 100 // cents to dollars
	}, [currentPrice])

	// Quarterly price for display (get the quarterly-priced equivalent)
	const quarterlyPriceDisplay = useMemo(() => {
		const quarterlyPrice = getPriceForPlan(
			selectedPlanId,
			'quarterly',
			effectiveSpend
		)
		if (!quarterlyPrice) return 0
		const monthlyVal =
			quarterlyPrice.pricing_by_currency?.usd?.price_monthly ??
			quarterlyPrice.price_monthly ??
			quarterlyPrice.price
		return monthlyVal / 100
	}, [getPriceForPlan, selectedPlanId, effectiveSpend])

	// Annual price for display (get the annually-priced equivalent)
	const annualPriceDisplay = useMemo(() => {
		const annualPrice = getPriceForPlan(
			selectedPlanId,
			'annually',
			effectiveSpend
		)
		if (!annualPrice) return 0
		const monthlyVal =
			annualPrice.pricing_by_currency?.usd?.price_monthly ??
			annualPrice.price_monthly ??
			annualPrice.price
		return monthlyVal / 100
	}, [getPriceForPlan, selectedPlanId, effectiveSpend])

	// Total price (actual charge) from the API — avoids rounding errors from price_monthly × multiplier
	const totalPriceDisplay = useMemo(() => {
		if (!currentPrice) return 0
		const total =
			currentPrice.pricing_by_currency?.usd?.price ?? currentPrice.price
		return total / 100
	}, [currentPrice])
	const effectiveCoupon: AppliedCoupon | null =
		campaign.active && campaign.quote
			? {
					code: 'BLACKFRIDAY',
					amountOff:
						campaign.quote.standard_annual_amount -
						campaign.quote.first_year_amount,
					label: 'Extra 15% off your first year',
					locked: true,
					firstYearOnly: true
			  }
			: campaign.active
			? {
					code: 'BLACKFRIDAY',
					amountOff: 0,
					locked: true,
					firstYearOnly: true
			  }
			: appliedCoupon

	const handleAdSpendChange = useCallback(
		(spend: number) => {
			if (isConfirming) return
			const previousSpend = adSpend
			setAdSpend(spend)

			// Lane fix (master only moves the plan UP): while the selected plan is
			// still the one we recommended, keep following the recommendation in
			// both directions, so lowering the spend from Custom or Pro comes back
			// down. A plan the person picked by hand stays put.
			if (selectedPlanId === recommendPlanFor(previousSpend)) {
				setSelectedPlanId(recommendPlanFor(spend))
				return
			}

			// When spend goes UP past the current plan's limit, force-select
			// the lowest eligible plan for the new spend value
			if (spend > previousSpend) {
				const currentPlanLimit = PLAN_SPEND_LIMITS[selectedPlanId] ?? Infinity
				if (spend > currentPlanLimit) {
					const lowestEligible = planOptions.find(
						(p) => spend <= (PLAN_SPEND_LIMITS[p.id] ?? Infinity)
					)
					if (lowestEligible) {
						setSelectedPlanId(lowestEligible.id)
					}
				}
			}
		},
		[adSpend, selectedPlanId, recommendPlanFor, isConfirming]
	)

	const handlePlanChange = useCallback(
		(planId: string) => {
			if (isConfirming) return
			setSelectedPlanId(planId)

			// PostHog: track plan click
			if (planId === 'custom') {
				posthog.capture('click-on-custom-plan')
			} else {
				posthog.capture(`click-on-${planId}-plan`)
			}

			// Backend: track plan selection
			if (accountId) {
				const isCustom = planId === 'custom'
				const price = isCustom
					? null
					: getPriceForPlan(planId, billingFrequency, effectiveSpend)
				try {
					Onboarding.updateSetup(accountId, {
						step: 'PLAN_SELECTED',
						selected_plan_details: {
							tier: isCustom ? null : planId,
							price_id: price?.price_id ?? null,
							price: price?.price ?? null,
							is_custom: isCustom,
							billing_period:
								billingFrequency === 'annually' ? 'yearly' : billingFrequency
						},
						selected_plan_at: new Date()
					})
				} catch (err) {
					console.error('Failed to track plan selection:', err)
				}
			}
		},
		[
			accountId,
			posthog,
			billingFrequency,
			effectiveSpend,
			getPriceForPlan,
			isConfirming
		]
	)

	const handleBillingFrequencyChange = useCallback(
		(frequency: BillingFrequency) => {
			if (isConfirming) return
			setBillingFrequency(frequency)
			selectedFrequency.current = frequency
			if (frequency !== 'quarterly' && appliedCoupon?.code === heroOfferCode)
				setAppliedCoupon(null)
			posthog.capture(`click-on-${frequency}-plan`)
		},
		[posthog, appliedCoupon, heroOfferCode, isConfirming]
	)

	const handleCompareAll = useCallback(() => {
		setCompareModalOpen(true)
	}, [])

	const handleContinueToPayment = useCallback(() => {
		paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [])

	const handlePaymentComplete = useCallback((isComplete: boolean) => {
		setIsPaymentComplete(isComplete)
	}, [])
	const handleConfirm = useCallback(async () => {
		if (submittingRef.current || isConfirming || !accountId) return
		submittingRef.current = true
		setPhase('submitting')
		setPaymentError(null)

		// With manual PaymentMethod creation mode, <Elements> is never bound to
		// an SI/PI clientSecret and stays mounted across every submit. There's
		// nothing to refresh, no form to clear, no remount-blink to manage.

		try {
			// 1. Create a fresh unattached PaymentMethod from the form contents.
			const confirmResult = await paymentFormRef.current?.confirmPayment()
			if (!confirmResult || confirmResult.kind !== 'ok') {
				// Stripe-side error (invalid card details, network, etc.).
				// PaymentElement stays mounted — user just edits and resubmits.
				return
			}
			const pmToken = confirmResult.pmToken

			// 2. PostHog: track trial start
			trackPostHogEvent(PostHogEvent.START_TRIAL, PostHogPath.SETUP, null)

			// 3. Activate trial via API
			if (isCustomPlan) {
				// Custom plan ($100k+): request quote
				if (customSpendValue < 100000) {
					toast.error(
						'Ad spend protection must be at least $100,000 for this plan'
					)
					return
				}
				const billingPeriod =
					billingFrequency === 'annually' ? 'yearly' : billingFrequency
				let quoteResult = await Onboarding.startQuote(accountId, {
					payment_method_id: pmToken,
					quote: { spend: customSpendValue, billing_period: billingPeriod },
					black_friday_selection_id: campaign.selectionId
				})

				if (quoteResult.kind === 'requires_action') {
					setPhase('verifying_3ds')
					const next = await paymentFormRef.current?.handleNextAction(
						quoteResult.client_secret
					)
					if (!next || !next.ok) {
						const rawMsg =
							next && !next.ok ? next.error : 'Card authentication failed'
						const looksCancelled = /cancel|abandon|dismiss/i.test(rawMsg)
						const msg = looksCancelled
							? 'Authentication was cancelled — please try again.'
							: rawMsg
						setPaymentError(msg)
						toast.error(msg)
						return
					}
					setPhase('finalizing')
					quoteResult = await Onboarding.startQuote(accountId, {
						payment_intent_id: quoteResult.payment_intent_id,
						quote: { spend: customSpendValue, billing_period: billingPeriod },
						black_friday_selection_id: campaign.selectionId
					})
					if (quoteResult.kind !== 'started') {
						const msg = 'Unexpected response after 3DS, please try again'
						setPaymentError(msg)
						toast.error(msg)
						return
					}
				}
				trackLeadFreeTrial({
					payment_method: 'card',
					value: 0,
					currency: 'USD',
					transaction_id: accountId,
					plan_name: 'custom',
					plan_price: 0,
					billing_cycle: billingPeriod,
					items: [
						{
							item_id: 'custom',
							item_name: 'custom',
							price: 0,
							quantity: 1
						}
					]
				})
			} else {
				// Standard plans (Lite/Standard/Pro)
				if (!currentPrice) {
					toast.error('Unable to determine pricing. Please try again.')
					return
				}

				let trialResult = await Onboarding.startTrial(accountId, {
					payment_method_id: pmToken,
					plan: { tier: selectedPlanId, price_id: currentPrice.price_id },
					// effectiveSpend is in thousands — the BE stores raw USD
					spend: effectiveSpend * 1000,
					coupon_code: appliedCoupon?.code || null,
					black_friday_selection_id: campaign.selectionId
				})

				if (trialResult.kind === 'requires_action') {
					setPhase('verifying_3ds')
					const next = await paymentFormRef.current?.handleNextAction(
						trialResult.client_secret
					)
					if (!next || !next.ok) {
						const rawMsg =
							next && !next.ok ? next.error : 'Card authentication failed'
						const looksCancelled = /cancel|abandon|dismiss/i.test(rawMsg)
						const msg = looksCancelled
							? 'Authentication was cancelled — please try again.'
							: rawMsg
						setPaymentError(msg)
						toast.error(msg)
						return
					}
					setPhase('finalizing')
					trialResult = await Onboarding.startTrial(accountId, {
						payment_intent_id: trialResult.payment_intent_id,
						plan: { tier: selectedPlanId, price_id: currentPrice.price_id },
						// Must be resent on the post-3DS retry — this call is what
						// actually starts the trial when the bank challenges the card
						spend: effectiveSpend * 1000,
						coupon_code: appliedCoupon?.code || null,
						black_friday_selection_id: campaign.selectionId
					})
					if (trialResult.kind !== 'started') {
						const msg = 'Unexpected response after 3DS, please try again'
						setPaymentError(msg)
						toast.error(msg)
						return
					}
				}
				const billingCycle =
					billingFrequency === 'annually' ? 'yearly' : billingFrequency
				trackLeadFreeTrial({
					payment_method: 'card',
					value: currentPrice.price / 100,
					currency: 'USD',
					transaction_id: accountId,
					plan_name: selectedPlanId,
					plan_price: currentPrice.price / 100,
					billing_cycle: billingCycle,
					items: [
						{
							item_id: currentPrice.price_id,
							item_name: selectedPlanId,
							price: currentPrice.price / 100,
							quantity: 1
						}
					]
				})
			}

			// 4. Post-activation: wait, track, navigate
			await onTrialActivated?.()
		} catch (err: any) {
			const errorMessage = err?.response?.data?.message
				? parseError(err)
				: 'Something went wrong. Please try again.'
			setPaymentError(errorMessage)
			toast.error(errorMessage)
		} finally {
			setPhase('idle')
			submittingRef.current = false
		}
	}, [
		isConfirming,
		accountId,
		isCustomPlan,
		selectedPlanId,
		billingFrequency,
		appliedCoupon,
		currentPrice,
		onTrialActivated,
		customSpendValue,
		campaign.selectionId,
		effectiveSpend
	])

	// CTA derived state — single source of truth for the mobile bottom button
	// AND the desktop sticky-overview button. Both should never disagree.
	// `canSubmit`: enabled when form is valid and we aren't already mid-flight.
	// `submitLabel`: visible button text. Phase-aware so users get continuous
	//   feedback during multi-second waits (the bank's 3DS modal can take
	//   5-15 seconds to load — "Processing..." for the whole thing feels broken).
	// `showSpinner`: render the inline loading spinner.
	const canSubmit =
		isPaymentComplete && !isConfirming && !campaign.loading && !campaign.error
	const submitLabel = (() => {
		if (phase === 'submitting') return 'Processing...'
		if (phase === 'verifying_3ds') return 'Waiting for your bank...'
		if (phase === 'finalizing') return 'Finalizing trial...'
		if (isCustomPlan) return 'Request quote and start trial'
		return 'Start 7-day free trial'
	})()
	const showSpinner = isConfirming

	const handleApplyCoupon = useCallback(async () => {
		if (!couponCode.trim() || couponLoading) return
		setCouponLoading(true)
		setCouponError(null)

		try {
			const { interval, interval_count } = INTERVAL_MAPPING[billingFrequency]
			const discount = await billingStore.fetchCoupon(couponCode.trim(), {
				billing_period:
					billingFrequency === 'annually' ? 'yearly' : billingFrequency,
				interval,
				interval_count,
				account_id: accountId || undefined
			})

			setAppliedCoupon({
				code: couponCode.trim(),
				percentOff: discount.percent_off ?? undefined,
				amountOff: discount.amount_off ?? undefined
			})
			setCouponCode('')
		} catch (err: any) {
			setCouponError(typeof err === 'string' ? err : 'Invalid coupon code')
		} finally {
			setCouponLoading(false)
		}
	}, [couponCode, couponLoading, billingFrequency, accountId, billingStore])

	useEffect(() => {
		if (
			!heroOfferCode ||
			autoAppliedOffer.current ||
			billingFrequency !== 'quarterly'
		)
			return
		autoAppliedOffer.current = true
		setCouponLoading(true)
		billingStore
			.fetchCoupon(heroOfferCode, {
				billing_period: 'quarterly',
				interval: 'month',
				interval_count: 3,
				account_id: accountId || undefined
			})
			.then((discount) => {
				if (selectedFrequency.current === 'quarterly')
					setAppliedCoupon({
						code: heroOfferCode,
						percentOff: discount.percent_off ?? undefined,
						amountOff: discount.amount_off ?? undefined
					})
			})
			.catch(() =>
				setCouponError(
					'Could not apply the special offer. Please try the code again.'
				)
			)
			.finally(() => setCouponLoading(false))
	}, [heroOfferCode, billingFrequency, accountId, billingStore])

	const handleRemoveCoupon = useCallback(() => {
		setAppliedCoupon(null)
		setCouponError(null)
	}, [])

	return (
		<div
			style={
				{ '--cg-top-banner-h': `${bannerHeight}px` } as React.CSSProperties
			}
			className="min-h-[calc(100vh_-_var(--cg-top-banner-h,0px))] bg-[var(--start-page-bg,#F2F2F2)] font-grotesk"
		>
			<div className="sticky top-0 z-[60]">
				<NewSubscriptionBanner
					open={campaign.showBanner}
					onHeightChange={setBannerHeight}
				/>
			</div>
			{campaign.error && (
				<p role="alert" className="px-5 py-2 text-red-600">
					{campaign.error}
				</p>
			)}
			{/* Header */}
			<OnboardingHeader variant="sign-up" />

			{/* Main Content */}
			<main className="lg:px-[65px] lg:pb-12 max-w-[1280px] mx-auto">
				<div className="flex lg:flex-row flex-col gap-[10px]">
					{/* Left Column — scrollable */}
					<div className="flex-1 min-w-0 p-[20px]">
						<div className="bg-white border border-t-checkbox rounded-[16px] overflow-hidden shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]">
							{/* Plan Selection */}
							<PlanSelectionCard
								selectedPlan={selectedPlan}
								plans={eligiblePlans}
								adSpend={adSpend}
								onAdSpendChange={handleAdSpendChange}
								onPlanChange={handlePlanChange}
								onCompareAll={handleCompareAll}
								recommendedPlanId={recommendedPlanId}
							/>

							{/* Divider */}
							<div className="mx-[24px] my-2 h-px bg-t-checkbox" />

							<div className="lg:hidden block">
								<PaymentOverview
									planName={selectedPlan.name}
									adSpendLimit={adSpendLimit}
									monthlyPrice={monthlyPriceDisplay}
									quarterlyPrice={quarterlyPriceDisplay}
									annualPrice={annualPriceDisplay}
									totalPrice={totalPriceDisplay}
									billingFrequency={billingFrequency}
									onBillingFrequencyChange={handleBillingFrequencyChange}
									onConfirm={handleConfirm}
									onContinueToPayment={handleContinueToPayment}
									couponCode={couponCode}
									onCouponCodeChange={setCouponCode}
									onApplyCoupon={handleApplyCoupon}
									hasTrial={true}
									adSpend={adSpend}
									isCustomPlan={isCustomPlan}
									canSubmit={canSubmit}
									submitLabel={submitLabel}
									showSpinner={showSpinner}
									selectedPmType={selectedPmType}
									customSpendValue={customSpendValue}
									onCustomSpendChange={(spend) => {
										if (!isConfirming) setCustomSpendValue(spend)
									}}
									appliedCoupon={effectiveCoupon}
									couponLoading={couponLoading}
									couponError={couponError}
									onRemoveCoupon={handleRemoveCoupon}
									paymentError={paymentError}
									renderCard={false}
								/>
							</div>
							<div className="lg:hidden block mx-[24px] my-2 mt-[24px] h-px bg-t-checkbox" />

							{/* Payment Form */}
							<div
								ref={paymentSectionRef}
								className="p-[24px] flex flex-col gap-[32px]"
							>
								<div className="flex flex-col gap-[6px]">
									<h2 className="font-grotesk font-medium text-[24px] leading-[30px] text-t-base-blue">
										Enter payment details to start your free trial
									</h2>
									<p className="font-grotesk text-[14px] leading-[18px] text-t-coupon">
										You will not be billed until your free trial ends.
									</p>
								</div>
								<PaymentForm
									ref={paymentFormRef}
									onPaymentComplete={handlePaymentComplete}
									onPaymentError={(e) => setPaymentError(e || null)}
									onSelectedPmTypeChange={setSelectedPmType}
								/>
							</div>
						</div>
					</div>

					{/* Right Column — sticky plan overview. A sticky column taller than
					    the viewport pins its bottom off-screen, and no page scroll brings
					    the button back; so the column is capped at the viewport and
					    scrolls inside itself when the window is short (no scrollbar:
					    the card is sized to fit 1280x680, this is the fallback). */}
					<div className="shrink-0 sticky top-[calc(72px_+_var(--cg-top-banner-h,0px))] self-start max-h-[calc(100vh_-_72px_-_var(--cg-top-banner-h,0px))] overflow-y-auto overscroll-contain plan-overview-scroll pt-[20px] pr-[20px] pb-[20px] lg:block hidden">
						<PaymentOverview
							planName={selectedPlan.name}
							adSpendLimit={adSpendLimit}
							monthlyPrice={monthlyPriceDisplay}
							quarterlyPrice={quarterlyPriceDisplay}
							annualPrice={annualPriceDisplay}
							totalPrice={totalPriceDisplay}
							billingFrequency={billingFrequency}
							onBillingFrequencyChange={handleBillingFrequencyChange}
							onConfirm={handleConfirm}
							onContinueToPayment={handleContinueToPayment}
							couponCode={couponCode}
							onCouponCodeChange={setCouponCode}
							onApplyCoupon={handleApplyCoupon}
							hasTrial={true}
							adSpend={adSpend}
							isCustomPlan={isCustomPlan}
							canSubmit={canSubmit}
							submitLabel={submitLabel}
							showSpinner={showSpinner}
							selectedPmType={selectedPmType}
							customSpendValue={customSpendValue}
							onCustomSpendChange={(spend) => {
								if (!isConfirming) setCustomSpendValue(spend)
							}}
							appliedCoupon={effectiveCoupon}
							couponLoading={couponLoading}
							couponError={couponError}
							onRemoveCoupon={handleRemoveCoupon}
							paymentError={paymentError}
						/>
					</div>
				</div>

				{/* Mobile bottom CTA — uses the same canSubmit/submitLabel/showSpinner
				    derived in PlanPaymentPage as the desktop sticky-overview button,
				    so the two CTAs cannot disagree. */}
				<div className="lg:hidden flex flex-col items-center w-full px-[20px]">
					<div className="pb-6 w-full flex flex-col gap-[8px]">
						<AppButton
							variant="marketing"
							size="lg"
							fullWidth
							label={submitLabel}
							leftIcon={
								showSpinner ? <DotMatrix size="xs" tone="inherit" /> : undefined
							}
							disabled={!canSubmit}
							onClick={handleConfirm}
						/>
						<TermsNote />
					</div>
				</div>
			</main>

			{/* Compare All Plans Modal */}
			<GDialog
				open={compareModalOpen}
				onClose={() => setCompareModalOpen(false)}
				maxWidth="5xl"
				showClose={true}
				paddingClassName="p-5"
			>
				<FeatureComparison
					adSpend={adSpend}
					onAdSpendChange={handleAdSpendChange}
					recommendedPlanId={recommendedPlanId}
					selectedPlanId={selectedPlanId}
					billingFrequency={billingFrequency}
					onBillingFrequencyChange={handleBillingFrequencyChange}
				/>
			</GDialog>
		</div>
	)
}
